import classNames from 'classnames';
import { motion } from 'framer-motion';
import React, {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import SkeletonLoader from '@/components/atoms/skeleton-loader/skeleton-loader';
import Typography from '@/components/atoms/typography/typography';
import Input from '@/components/molecules/input/input';
import InspirationalSearchEntryPoint from '@/components/molecules/inspirational-search-entry-point/inspirational-search-entry-point';
import FilterCardList, {
  FilterItem,
} from '@/components/organisms/filter-card-list/filter-card-list';
import FilterChipList from '@/components/organisms/filter-chip-list/filter-chip-list';
import {
  LocationChangeParameters,
  LocationGroup,
} from '@/components/organisms/search-bar/search-bar.types';
import Link from '@/components/utilities/link/link';
import { useI18n } from '@/libraries/contexts/i18n-context';
import { useWindowSize } from '@/libraries/hooks/use-window-size';
import SearchBarLocationsCheckboxWrapper from '../search-bar-locations-checkbox-wrapper/search-bar-locations-checkbox-wrapper';
import SearchBarLocationsSearchResults, {
  MINIMUM_SEARCH_STRING_LENGTH,
} from '../search-bar-locations-search-results/search-bar-locations-search-results';
import SearchBarSectionMobileWrapper from '../search-bar-section-mobile-wrapper/search-bar-section-mobile-wrapper';
import styles from './search-bar-locations-dropdown.module.scss';

export interface SearchBarLocationsDropdownProps {
  /** The class name to apply */
  className?: string;
  /** The description to show in the mobile wrapper */
  description?: string | string[];
  /** Indicates whether the dropdown is in a loading state */
  isLoading?: boolean;
  /** Indicates whether the dropdown is visible */
  isVisible?: boolean;
  /** Indicates whether the dropdown is used in the search widget */
  isWidget?: boolean;
  /** The label to show in the mobile wrapper */
  label: string;
  /** The locations to be displayed */
  locations: LocationGroup[];
  /** An event that is triggered on change of locations */
  onChangeLocations?: ({
    destinationRegions,
    destinations,
    hotels,
    locations,
  }: LocationChangeParameters) => void;
  /** An event that is triggered on change of transportation filter */
  onChangeTransportation?: (id: string) => void;
  /** An event that is triggered on click back */
  onClickBack?: () => void;
  /** An event that is triggered on click close */
  onClickClose?: () => void;
  /** An event that is triggered on click next */
  onClickNext?: () => void;
  /** The period type */
  periodType?: Content.Components['PeriodType'];
  /** The search settings */
  searchSettings?:
    | Content.Components['SearchSettingsDto']
    | Content.Components['TravelPortalSearchSettingsDto'];
  /** The options for transportation */
  transportationOptions: FilterItem[];
}

const SearchBarLocationsDropdown = memo(
  forwardRef(
    (
      {
        className,
        description,
        isLoading = false,
        isVisible = false,
        isWidget = false,
        label,
        locations = [],
        onChangeLocations,
        onChangeTransportation,
        onClickBack,
        onClickClose,
        onClickNext,
        periodType,
        searchSettings,
        transportationOptions = [],
      }: SearchBarLocationsDropdownProps,
      ref,
    ) => {
      const { t } = useI18n();
      const isLaptop = useWindowSize('laptop');
      const isTravelPortal =
        searchSettings?.$type === 'TravelPortalSearchSettingsDto';
      const showInspirationalEntryPoint =
        !isWidget &&
        searchSettings?.$type === 'SearchSettingsDto' &&
        searchSettings.searchBarInspirationalSearchEntryPoint;
      const [searchString, setSearchString] = useState('');

      const [selectedDestinations, setSelectedDestinations] = useState<
        Content.Components['SearchDestinationNode'][]
      >([]);

      const handleClickClear = useCallback(() => {
        const updatedLocations = locations.map(locationGroup => ({
          ...locationGroup,
          options: locationGroup.options.map(option => ({
            ...option,
            checked: false,
          })),
        }));

        onChangeLocations?.({
          destinationRegions: [],
          destinations: [],
          hotels: [],
          locations: updatedLocations,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [locations]);

      const parsedDestinations = useMemo(() => {
        const parsed = selectedDestinations.reduce(
          (acc, destination) => {
            const { nodeType, id, name } = destination;

            if (nodeType === 'hotel') {
              acc.hotels.push(id.toString());
            } else if (nodeType === 'destinationRegion') {
              acc.destinationRegions.push(id.toString());
            } else if (nodeType === 'country') {
              acc.countries.push(name);
            } else if (nodeType === 'destination') {
              acc.destinations.push(id.toString());
            }

            return acc;
          },
          {
            countries: [] as string[],
            destinationRegions: [] as string[],
            destinations: [] as string[],
            hotels: [] as string[],
          },
        );

        return parsed;
      }, [selectedDestinations]);

      const handleOnClickDestination = useCallback(
        (destination: Content.Components['SearchDestinationNode']) => {
          setSearchString('');

          setSelectedDestinations(previousDestinations => {
            const isAlreadySelected = previousDestinations.some(
              previousDestination => previousDestination.id === destination.id,
            );

            if (isAlreadySelected) {
              return previousDestinations;
            }

            return [...previousDestinations, destination];
          });
        },
        [],
      );

      const handleOnClickRemoveChip = useCallback(
        (destination: { id: string; label: string; nodeType: string }) => {
          if (!destination) return;

          setSelectedDestinations(previousDestinations => {
            const newDestinations = previousDestinations.filter(
              previousDestination =>
                previousDestination.id.toString() !== destination.id,
            );

            return newDestinations;
          });
        },
        [],
      );

      const syncLocations = useCallback(
        (updatedLocations: LocationGroup[] | null = null) => {
          const baseLocations = updatedLocations ?? locations;

          const parsedBaseLocations = {
            destinationRegions: [] as string[],
            destinations: [] as string[],
            hotels: [] as string[],
          };

          for (const group of baseLocations) {
            for (const option of group.options) {
              if (!option.checked) continue;

              switch (option.nodeType) {
                case 'destinationRegion':
                  parsedBaseLocations.destinationRegions.push(option.id);
                  break;

                case 'destination':
                  parsedBaseLocations.destinations.push(option.id);
                  break;

                case 'hotel':
                  parsedBaseLocations.hotels.push(option.id);
                  break;
              }
            }
          }

          const merged = {
            destinationRegions: Array.from(
              new Set([
                ...parsedDestinations.destinationRegions,
                ...parsedBaseLocations.destinationRegions,
              ]),
            ),
            destinations: Array.from(
              new Set([
                ...parsedDestinations.destinations,
                ...parsedBaseLocations.destinations,
              ]),
            ),
            hotels: Array.from(
              new Set([
                ...parsedDestinations.hotels,
                ...parsedBaseLocations.hotels,
              ]),
            ),
          };

          onChangeLocations?.({
            destinationRegions: merged.destinationRegions,
            destinations: merged.destinations,
            hotels: merged.hotels,
            locations: baseLocations,
          });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [locations, parsedDestinations],
      );

      useEffect(() => {
        const { countries, destinationRegions, destinations } =
          parsedDestinations;

        const updatedLocations = locations.map(locationGroup => {
          // if search result is a country, select all destinations in that country
          if (countries.includes(locationGroup.groupName)) {
            return {
              ...locationGroup,
              options: locationGroup.options.map(option => ({
                ...option,
                checked: true,
              })),
            };
          }

          // if search result is present in the options, check the option
          const updatedOptions = locationGroup.options.map(option => {
            if (destinations.includes(option.id.toString())) {
              return {
                ...option,
                checked: true,
              };
            }

            if (destinationRegions.includes(option.id.toString())) {
              return {
                ...option,
                checked: true,
              };
            }

            return option;
          });

          return {
            ...locationGroup,
            options: updatedOptions,
          };
        });

        syncLocations(updatedLocations);
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [selectedDestinations]);

      const handleLocationsUpdate = useCallback(
        (updatedLocations: LocationGroup[]) => {
          syncLocations(updatedLocations);
        },
        [syncLocations],
      );

      const filterSelectedDestinations = useMemo(
        () =>
          selectedDestinations
            .filter(destination => {
              // if node type is a hotel
              if (destination.nodeType === 'hotel') {
                return true;
              }

              // if node type is a destination that is not in the locations options
              const isDestinationInLocations = locations.some(locationGroup =>
                locationGroup.options.some(
                  option => option.id.toString() === destination.id.toString(),
                ),
              );

              return !isDestinationInLocations;
            })
            .map(destination => ({
              ...destination,
              id: destination.id.toString(),
              label: destination.name,
            })),
        [selectedDestinations, locations],
      );

      const renderLocations = useCallback(
        () => (
          <React.Fragment>
            <div className={styles.transportation}>
              <div
                className={classNames(styles.label, {
                  [styles.isWidget]: isWidget,
                })}
              >
                <Typography tag="label" tagStyle="labelMedium">
                  {t('items.search.searchBar.location.chooseTransportation')}
                </Typography>
              </div>

              {isLoading ? (
                <SkeletonLoader borderRadius="4px" height="30px" width="100%" />
              ) : (
                <FilterCardList
                  displayVariant={isWidget ? 'column' : 'row'}
                  items={transportationOptions}
                  onChange={onChangeTransportation}
                />
              )}
            </div>

            {!isWidget && (
              <div className={styles.locations}>
                {!isTravelPortal && (
                  <div className={styles.searchInputWrapper}>
                    <Typography
                      className={styles.destinationsLabel}
                      tag="label"
                      tagStyle="labelMedium"
                    >
                      {t('items.search.searchBar.location.chooseDestination')}
                    </Typography>

                    <Input
                      className={styles.searchInput}
                      hasClear
                      icon="Search"
                      onChange={event => {
                        setSearchString(event.target.value);
                      }}
                      onClear={() => setSearchString('')}
                      placeholder={t(
                        'items.search.searchBar.location.searchPlaceholder',
                      )}
                      value={searchString}
                    />
                  </div>
                )}

                {selectedDestinations.length > 0 && (
                  <div className={styles.destinationChipsWrapper}>
                    <FilterChipList
                      clearLabel={t(
                        'items.search.searchBar.location.clearSelectedDestinations',
                      )}
                      items={filterSelectedDestinations}
                      onClickRemoveChip={handleOnClickRemoveChip}
                    />
                  </div>
                )}

                <div className={styles.locationsWrapper}>
                  {!isTravelPortal &&
                  searchString.length >= MINIMUM_SEARCH_STRING_LENGTH ? (
                    <SearchBarLocationsSearchResults
                      campaignId={
                        searchSettings?.campaignId?.toString() ?? undefined
                      }
                      departureIds={
                        transportationOptions.find(option => option.isSelected)
                          ?.id
                      }
                      onClickDestination={handleOnClickDestination}
                      periodType={periodType}
                      searchString={searchString}
                    />
                  ) : (
                    <React.Fragment>
                      {isLoading ? (
                        <div className={styles.loadingWrapper}>
                          {Array.from({ length: 24 }).map((_, loaderIndex) => (
                            <SkeletonLoader
                              key={loaderIndex}
                              borderRadius="4px"
                              height="20px"
                              width="100%"
                            />
                          ))}
                        </div>
                      ) : (
                        <SearchBarLocationsCheckboxWrapper
                          locations={locations}
                          masonrySettings={searchSettings?.masonrySettings}
                          onUpdateLocations={handleLocationsUpdate}
                          useMasonryLayout={searchSettings?.useMasonryLayout}
                        />
                      )}
                    </React.Fragment>
                  )}
                </div>

                {locations.length > 0 && (
                  <div className={styles.linkButtonWrapper}>
                    <Link onClick={handleClickClear}>
                      <Typography className={styles.link} tagStyle="bodyMedium">
                        {t('items.common.words.clear')}
                      </Typography>
                    </Link>
                  </div>
                )}
              </div>
            )}
          </React.Fragment>
        ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
          isWidget,
          searchString,
          selectedDestinations,
          locations,
          transportationOptions,
          isLoading,
          periodType,
          searchSettings,
          filterSelectedDestinations,
        ],
      );

      useImperativeHandle(ref, () => ({
        clear: handleClickClear,
      }));

      if (isLaptop && !isVisible) {
        return null;
      }

      return (
        <div
          className={classNames(styles.component, className, {
            [styles.isWidget]: isWidget,
          })}
        >
          {isLaptop ? (
            <motion.div
              animate={isVisible ? 'show' : 'hidden'}
              className={classNames(styles.desktopWrapper, {
                [styles.isVisible]: isVisible,
              })}
              exit="hidden"
              initial="hidden"
              transition={{ duration: 0.25, ease: 'easeInOut' }}
              variants={{
                hidden: {
                  opacity: 0,
                  transitionEnd: {
                    display: 'none',
                  },
                  translateY: 0,
                },
                show: {
                  display: 'block',
                  opacity: 1,
                  translateY: 0,
                },
              }}
            >
              <div className={styles.content}>
                {renderLocations()}

                {showInspirationalEntryPoint && (
                  <InspirationalSearchEntryPoint
                    className={styles.inspirationalSearchEntryPoint}
                    {...searchSettings.searchBarInspirationalSearchEntryPoint}
                    variant="solid"
                  />
                )}
              </div>
            </motion.div>
          ) : (
            <SearchBarSectionMobileWrapper
              buttonLabel={t('items.common.words.next')}
              description={description}
              isOpen={isVisible}
              label={label}
              onClickBack={onClickBack}
              onClickClear={handleClickClear}
              onClickClose={onClickClose}
              onClickNext={onClickNext}
              searchSettings={searchSettings}
              showBackButton
            >
              {renderLocations()}
            </SearchBarSectionMobileWrapper>
          )}
        </div>
      );
    },
  ),
);

SearchBarLocationsDropdown.displayName = 'SearchBarLocationsDropdown';

export default SearchBarLocationsDropdown;
